import React, { useState, useMemo } from 'react';
import { useGetMatieresByFiltersQuery } from '../../services/matieresApi';
import { useGetNotesDevoirsQuery, useGetStatsQuery } from '../../services/notesDevoirsApi';
import { useGetChoixFormulaireQuery } from '../../services/etudiantsApi';
import { useGetClassesQuery } from '../../services/polesOptionsApi';
import { RapportNotesPreview } from '../../components/RapportNotes/RapportNotesPreview';
import { formatNote } from '../../utils/formatNote';
import { SectionCard } from '../../components/ui/SectionCard';
import {
  Search,
  Printer,
  RefreshCw,
  Lock,
  Filter,
  BarChart3,
  Loader,
  AlertTriangle,
  FileBarChart,
} from 'lucide-react';

interface Props {
  anneeAcademique: string;
}

const VisualisationNotes: React.FC<Props> = ({ anneeAcademique }) => {
  const { data: choixFormulaire } = useGetChoixFormulaireQuery();
  const { data: allClasses = [] } = useGetClassesQuery({ actif: true });

  const [niveau, setNiveau] = useState('');
  const [classe, setClasse] = useState('');
  const [poleFilter, setPoleFilter] = useState('');
  const [matiereId, setMatiereId] = useState<number | null>(null);
  const [semestre, setSemestre] = useState<'S1' | 'S2'>('S1');
  const [search, setSearch] = useState('');
  const [filterVerrouille, setFilterVerrouille] = useState<'all' | 'true' | 'false'>('all');
  const [sortField, setSortField] = useState<'nom' | 'matricule' | 'moyenne' | 'devoir1' | 'devoir2'>('nom');
  const [sortAsc, setSortAsc] = useState(true);
  const [showRapportModal, setShowRapportModal] = useState(false);

  // Niveaux depuis choixFormulaire
  const niveaux = useMemo(() => {
    if (!choixFormulaire?.niveaux) return [];
    return choixFormulaire.niveaux.filter(n => n.count > 0).map(n => n.code);
  }, [choixFormulaire]);

  // Classes filtrées par niveau et pôle
  const classes = useMemo(() => {
    if (!niveau) return [];
    let filtered = allClasses.filter(c => c.niveau === niveau);
    if (poleFilter) {
      filtered = filtered.filter(c => c.pole_nom === poleFilter);
    }
    return filtered.map(c => ({ code: c.code, label: `${c.code} — ${c.option_nom}`, option_nom: c.option_nom, count: 0 }));
  }, [allClasses, niveau, poleFilter]);

  // Option de la classe sélectionnée
  const selectedOptionNom = useMemo(() => {
    if (!classe) return undefined;
    const found = classes.find(c => c.code === classe);
    return found?.option_nom;
  }, [classe, classes]);

  // Convertir niveau + S1/S2 en numéro de semestre backend (1-6)
  const semestreNum = useMemo(() => {
    const map: Record<string, Record<string, number>> = {
      'L1': { 'S1': 1, 'S2': 2 },
      'L2': { 'S1': 3, 'S2': 4 },
      'L3': { 'S1': 5, 'S2': 6 },
    };
    return map[niveau]?.[semestre];
  }, [niveau, semestre]);

  // Matières filtrées par niveau, option et semestre
  const { data: matieres } = useGetMatieresByFiltersQuery(
    {
      niveau: niveau || undefined,
      option: selectedOptionNom,
      semestre: semestreNum,
    },
    { skip: !niveau || !selectedOptionNom }
  );

  const queryParams = useMemo(
    () => ({
      ...(matiereId ? { matiere_id: matiereId } : {}),
      annee_academique: anneeAcademique,
      semestre,
      ...(filterVerrouille !== 'all' ? { verrouille: filterVerrouille === 'true' } : {}),
    }),
    [matiereId, anneeAcademique, semestre, filterVerrouille]
  );

  const { data: notes, isLoading, refetch } = useGetNotesDevoirsQuery(
    anneeAcademique ? queryParams : {},
    { skip: !anneeAcademique }
  );

  const { data: stats } = useGetStatsQuery(
    matiereId ? { matiere_id: matiereId, annee_academique: anneeAcademique, semestre } : {},
    { skip: !matiereId || !anneeAcademique }
  );

  const filteredNotes = useMemo(() => {
    if (!notes) return [];
    let result = [...notes];

    // Filtre par texte
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (n) =>
          n.etudiant_matricule?.toLowerCase().includes(q) ||
          n.etudiant_nom?.toLowerCase().includes(q) ||
          n.matiere_nom?.toLowerCase().includes(q) ||
          n.matiere_code?.toLowerCase().includes(q)
      );
    }

    // Tri
    result.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'nom') {
        cmp = (a.etudiant_nom || '').localeCompare(b.etudiant_nom || '');
      } else if (sortField === 'matricule') {
        cmp = (a.etudiant_matricule || '').localeCompare(b.etudiant_matricule || '');
      } else if (sortField === 'moyenne') {
        const ma = a.moyenne_devoirs != null ? parseFloat(String(a.moyenne_devoirs)) : -1;
        const mb = b.moyenne_devoirs != null ? parseFloat(String(b.moyenne_devoirs)) : -1;
        cmp = ma - mb;
      } else if (sortField === 'devoir1') {
        const da = a.note1 != null ? parseFloat(String(a.note1)) : -1;
        const db = b.note1 != null ? parseFloat(String(b.note1)) : -1;
        cmp = da - db;
      } else if (sortField === 'devoir2') {
        const da = a.note2 != null ? parseFloat(String(a.note2)) : -1;
        const db = b.note2 != null ? parseFloat(String(b.note2)) : -1;
        cmp = da - db;
      }
      return sortAsc ? cmp : -cmp;
    });

    return result;
  }, [notes, search, sortField, sortAsc]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) setSortAsc((v) => !v);
    else { setSortField(field); setSortAsc(true); }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) return <span className="ml-1 text-gray-300 dark:text-gray-600">↕</span>;
    return <span className="ml-1 text-blue-500">{sortAsc ? '↑' : '↓'}</span>;
  };

  const selectedMatiere = Array.isArray(matieres) ? matieres.find((m) => m.id === matiereId) : undefined;

  // Stats calculées localement si API stats indisponible
  const localStats = useMemo(() => {
    if (!filteredNotes.length) return null;
    const avecNote = filteredNotes.filter((n) => n.moyenne_devoirs != null);
    const avg = avecNote.length
      ? avecNote.reduce((acc, n) => acc + parseFloat(String(n.moyenne_devoirs!)), 0) / avecNote.length
      : 0;
    return {
      total: filteredNotes.length,
      avecMoyenne: avecNote.length,
      moyenneGenerale: avg,
      absentsD1: filteredNotes.filter((n) => n.absent1).length,
      absentsD2: filteredNotes.filter((n) => n.absent2).length,
      verrouilles: filteredNotes.filter((n) => n.verrouille).length,
    };
  }, [filteredNotes]);

  const handlePrint = () => {
    const printContent = document.getElementById('notes-print-table');
    if (!printContent) return;

    const matiereName = selectedMatiere?.nom_matiere || 'Toutes matières';
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Notes de Devoirs</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; color: #000; }
          h1 { font-size: 14px; margin-bottom: 4px; }
          p.subtitle { font-size: 11px; color: #444; margin-bottom: 12px; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #e5e7eb; padding: 6px 8px; text-align: left; font-size: 10px; text-transform: uppercase; border: 1px solid #d1d5db; }
          td { padding: 5px 8px; border: 1px solid #e5e7eb; }
          tr:nth-child(even) td { background: #f9fafb; }
          .num { text-align: right; font-variant-numeric: tabular-nums; }
          .center { text-align: center; }
          .stats-bar { display: flex; gap: 24px; margin-top: 14px; padding: 8px; background: #f3f4f6; border: 1px solid #d1d5db; font-size: 11px; }
          .stat { display: flex; flex-direction: column; }
          .stat-val { font-size: 15px; font-weight: bold; }
          .stat-lbl { color: #6b7280; font-size: 10px; }
          @media print {
            button { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>Notes de Devoirs — ${matiereName}</h1>
        <p class="subtitle">
          ${classe ? `Classe : ${classe} &nbsp;|&nbsp;` : ''}
          Semestre : ${semestre} &nbsp;|&nbsp;
          Année : ${anneeAcademique} &nbsp;|&nbsp;
          Imprimé le : ${new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </p>
        ${printContent.outerHTML}
        ${localStats ? `
        <div class="stats-bar">
          <div class="stat"><span class="stat-val">${localStats.total}</span><span class="stat-lbl">Étudiants</span></div>
          <div class="stat"><span class="stat-val">${localStats.moyenneGenerale.toFixed(2)}</span><span class="stat-lbl">Moyenne classe</span></div>
          <div class="stat"><span class="stat-val">${localStats.absentsD1}</span><span class="stat-lbl">Absents D1</span></div>
          <div class="stat"><span class="stat-val">${localStats.absentsD2}</span><span class="stat-lbl">Absents D2</span></div>
          <div class="stat"><span class="stat-val">${localStats.verrouilles}</span><span class="stat-lbl">Verrouillés</span></div>
        </div>` : ''}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  return (
    <div className="space-y-5">
      {/* Filtres (unifié — refonte vague 3 lot 2) */}
      <SectionCard title="Filtres" icon={<Filter size={16} />} padding="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
          {/* Pôle */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Pôle
            </label>
            <select
              value={poleFilter}
              onChange={(e) => { setPoleFilter(e.target.value); setClasse(''); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous</option>
              {['Polytechnique', 'Commerce', 'Juridique'].map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          {/* Niveau */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Niveau
            </label>
            <select
              value={niveau}
              onChange={(e) => { setNiveau(e.target.value); setClasse(''); setMatiereId(null); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous</option>
              {niveaux.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          {/* Classe */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Classe
            </label>
            <select
              value={classe}
              onChange={(e) => { setClasse(e.target.value); setMatiereId(null); }}
              disabled={!niveau}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600"
            >
              <option value="">Toutes</option>
              {classes.map((c) => (
                <option key={c.code} value={c.code}>{c.label} ({c.count})</option>
              ))}
            </select>
          </div>

          {/* Matière */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Matière
            </label>
            <select
              value={matiereId || ''}
              onChange={(e) => setMatiereId(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Toutes</option>
              {(matieres || []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code_matiere} – {m.nom_matiere}
                </option>
              ))}
            </select>
          </div>

          {/* Semestre */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Semestre
            </label>
            <select
              value={semestre}
              onChange={(e) => setSemestre(e.target.value as 'S1' | 'S2')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="S1">Semestre 1</option>
              <option value="S2">Semestre 2</option>
            </select>
          </div>

          {/* Verrouillage */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Verrouillage
            </label>
            <select
              value={filterVerrouille}
              onChange={(e) => setFilterVerrouille(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous</option>
              <option value="false">Non verrouillés</option>
              <option value="true">Verrouillés</option>
            </select>
          </div>

          {/* Recherche */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Recherche
            </label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Matricule, nom…"
                className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Statistiques */}
      {localStats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total notes', value: localStats.total, color: 'text-gray-900 dark:text-gray-100' },
            { label: 'Avec moyenne', value: localStats.avecMoyenne, color: 'text-blue-600 dark:text-blue-400' },
            { label: 'Moy. générale', value: formatNote(localStats.moyenneGenerale), color: localStats.moyenneGenerale >= 10 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400' },
            { label: 'Absents D1', value: localStats.absentsD1, color: 'text-amber-600 dark:text-amber-400' },
            { label: 'Absents D2', value: localStats.absentsD2, color: 'text-amber-600 dark:text-amber-400' },
            { label: 'Verrouillés', value: localStats.verrouilles, color: 'text-red-600 dark:text-red-400' },
          ].map((s) => (
            <div key={s.label} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">{s.label}</div>
              <div className={`text-xl font-bold mt-0.5 ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tableau */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 size={18} className="text-blue-500" />
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {selectedMatiere
                ? `${selectedMatiere.nom_matiere} (${selectedMatiere.code_matiere})`
                : 'Toutes les matières'}
              {classe && <span className="ml-2 text-sm text-gray-400 font-normal">— {classe}</span>}
            </h3>
            {!isLoading && (
              <span className="ml-2 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full text-xs">
                {filteredNotes.length} ligne(s)
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refetch}
              className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              title="Actualiser"
            >
              <RefreshCw size={15} />
            </button>
            <button
              onClick={handlePrint}
              disabled={filteredNotes.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              <Printer size={15} />
              Imprimer
            </button>
            <button
              onClick={() => setShowRapportModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
            >
              <FileBarChart size={15} />
              Rapport par groupe
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader size={32} className="animate-spin text-blue-500 mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Chargement des notes…</p>
          </div>
        ) : !anneeAcademique ? (
          <div className="flex flex-col items-center justify-center py-16 text-amber-600 dark:text-amber-400">
            <AlertTriangle size={32} className="mb-3" />
            <p className="text-sm">Année académique non définie.</p>
          </div>
        ) : filteredNotes.length === 0 ? (
          <div className="py-16 text-center text-gray-400 dark:text-gray-500">
            <BarChart3 size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">
              {notes?.length === 0
                ? 'Aucune note saisie pour ces critères.'
                : 'Aucun résultat pour cette recherche.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table id="notes-print-table" className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <th className="px-4 py-3 text-left">#</th>
                  <th
                    className="px-4 py-3 text-left cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => handleSort('matricule')}
                  >
                    Matricule <SortIcon field="matricule" />
                  </th>
                  <th
                    className="px-4 py-3 text-left cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => handleSort('nom')}
                  >
                    Nom <SortIcon field="nom" />
                  </th>
                  {!matiereId && (
                    <th className="px-4 py-3 text-left">Matière</th>
                  )}
                  <th
                    className="px-4 py-3 text-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => handleSort('devoir1')}
                  >
                    Devoir 1 <SortIcon field="devoir1" />
                  </th>
                  <th className="px-4 py-3 text-center">Abs D1</th>
                  <th
                    className="px-4 py-3 text-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => handleSort('devoir2')}
                  >
                    Devoir 2 <SortIcon field="devoir2" />
                  </th>
                  <th className="px-4 py-3 text-center">Abs D2</th>
                  <th
                    className="px-4 py-3 text-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => handleSort('moyenne')}
                  >
                    Moyenne <SortIcon field="moyenne" />
                  </th>
                  <th className="px-4 py-3 text-center">Statut</th>
                  <th className="px-4 py-3 text-left text-gray-400">Saisi par</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filteredNotes.map((note, idx) => {
                  const moy = note.moyenne_devoirs != null ? parseFloat(String(note.moyenne_devoirs)) : null;
                  const d1 = note.note1 != null ? parseFloat(String(note.note1)) : null;
                  const d2 = note.note2 != null ? parseFloat(String(note.note2)) : null;
                  return (
                    <tr
                      key={note.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="px-4 py-2.5 text-gray-400 dark:text-gray-500 tabular-nums text-xs">
                        {idx + 1}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs text-gray-700 dark:text-gray-300">
                        {note.etudiant_matricule}
                      </td>
                      <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                        {note.etudiant_nom}
                      </td>
                      {!matiereId && (
                        <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400 text-xs">
                          <span className="font-mono text-gray-400">{note.matiere_code}</span>{' '}
                          {note.matiere_nom}
                        </td>
                      )}
                      {/* D1 */}
                      <td className="px-4 py-2.5 text-center tabular-nums">
                        {note.absent1 ? (
                          <span className="text-amber-600 dark:text-amber-400 text-xs font-medium">ABS</span>
                        ) : d1 !== null ? (
                          <span className={d1 >= 10 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                            {formatNote(d1)}
                          </span>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {note.absent1 && (
                          <span className="inline-block w-2 h-2 bg-amber-400 rounded-full" />
                        )}
                      </td>
                      {/* D2 */}
                      <td className="px-4 py-2.5 text-center tabular-nums">
                        {note.absent2 ? (
                          <span className="text-amber-600 dark:text-amber-400 text-xs font-medium">ABS</span>
                        ) : d2 !== null ? (
                          <span className={d2 >= 10 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                            {formatNote(d2)}
                          </span>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {note.absent2 && (
                          <span className="inline-block w-2 h-2 bg-amber-400 rounded-full" />
                        )}
                      </td>
                      {/* Moyenne */}
                      <td className="px-4 py-2.5 text-center font-bold tabular-nums">
                        {moy !== null ? (
                          <span
                            className={
                              moy >= 10
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400'
                            }
                          >
                            {moy.toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600">—</span>
                        )}
                      </td>
                      {/* Statut */}
                      <td className="px-4 py-2.5 text-center">
                        {note.verrouille ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full text-xs font-medium">
                            <Lock size={10} /> Verrouillé
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-full text-xs">
                            Éditable
                          </span>
                        )}
                      </td>
                      {/* Saisi par */}
                      <td className="px-4 py-2.5 text-xs text-gray-400 dark:text-gray-500">
                        {note.saisi_par_nom || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {filteredNotes.length > 0 && localStats && (
                <tfoot>
                  <tr className="bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-200 dark:border-gray-600 text-xs font-semibold">
                    <td colSpan={matiereId ? 3 : 4} className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      Total : {localStats.total} note(s)
                    </td>
                    <td className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                      {filteredNotes.filter((n) => !n.absent1 && n.note1 != null).length > 0
                        ? (filteredNotes
                            .filter((n) => !n.absent1 && n.note1 != null)
                            .reduce((acc, n) => acc + parseFloat(String(n.note1!)), 0) /
                            filteredNotes.filter((n) => !n.absent1 && n.note1 != null).length
                          ).toFixed(2)
                        : '—'}
                    </td>
                    <td />
                    <td className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                      {filteredNotes.filter((n) => !n.absent2 && n.note2 != null).length > 0
                        ? (filteredNotes
                            .filter((n) => !n.absent2 && n.note2 != null)
                            .reduce((acc, n) => acc + parseFloat(String(n.note2!)), 0) /
                            filteredNotes.filter((n) => !n.absent2 && n.note2 != null).length
                          ).toFixed(2)
                        : '—'}
                    </td>
                    <td />
                    <td className="px-4 py-3 text-center text-gray-700 dark:text-gray-200">
                      {localStats.moyenneGenerale.toFixed(2)}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* Modal Rapport par groupe */}
      {showRapportModal && (
        <RapportNotesPreview onClose={() => setShowRapportModal(false)} />
      )}
    </div>
  );
};

export default VisualisationNotes;
